import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  AiModelRecord,
  AiProviderId,
  AiProviderKeyStatusRecord,
  CompanyProfileCollectionRecord,
  CompanyProfileRecord,
  ContractTemplateCollectionRecord,
  CustomerCategoryCollectionRecord,
  CustomerCategoryRecord,
  OblioDiagnosticsRecord,
  ThemeBridgeSourceStatus,
  ThemeBridgeStatusRecord,
  WorkspaceSettingsRecord
} from '@shared/types';
import { useShellStore } from '@renderer/app/state/shell-store';
import { Icon, normalizeBusinessIconName } from '@renderer/shared/components/icons';
import {
  EditorTabs,
  EmptyState,
  IconPickerField,
  PanelCard,
  SectionHeader,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  ValuePair
} from '@renderer/shared/components/primitives';
import { SplitWorkspaceLayout } from '@renderer/shared/components/split-workspace';
import { useI18n } from '@renderer/shared/i18n';
import {
  clearAiCache,
  getAiProviderKeyStatus,
  listAiModels,
  setAiProviderKey,
  testAiProviderKey
} from '@renderer/domains/ai/ai-repository';
import {
  getWordPressBridgeContract,
  getWordPressBridgeStatus,
  getWorkspaceSettings,
  listCompanyProfiles,
  listCustomerCategories,
  listContractTemplates,
  pickDirectory,
  refreshOblioNomenclature,
  refreshWordPressBridgeStatus,
  saveCompanyProfile,
  saveCustomerCategory,
  testOblioConnection,
  updateWorkspaceIntegrationSecrets,
  updateWorkspaceLocalEnvironmentSecrets,
  updateWorkspacePaymentSecrets,
  updateWorkspaceSettings
} from './settings-repository';
import { selectCompanyProfileDraft, selectCustomerCategoryDraft, selectSettingsTabs } from './settings-selectors';
import { useSettingsViewStore } from './settings-view-store';

function toNullable(value: string): string | null {
  return value.trim().length > 0 ? value : null;
}

const EMPTY_AI_KEY_STATUS: AiProviderKeyStatusRecord = {
  anthropic: { state: 'not_set', message: null },
  openai: { state: 'not_set', message: null }
};

function getAiStatusLabel(
  status: AiProviderKeyStatusRecord[AiProviderId],
  t: ReturnType<typeof useI18n>['t']
): string {
  return status.state === 'saved'
    ? t('common.saved')
    : status.state === 'invalid'
      ? t('common.invalid')
      : t('common.notSet');
}

function getAiStatusTone(status: AiProviderKeyStatusRecord[AiProviderId]): 'neutral' | 'success' | 'warning' {
  return status.state === 'saved'
    ? 'success'
    : status.state === 'invalid'
      ? 'warning'
      : 'neutral';
}

export function SettingsScreen(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const shellSettings = useShellStore((state) => state.settings);
  const shellPreferences = useShellStore((state) => state.preferences);
  const saveSettings = useShellStore((state) => state.saveSettings);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const {
    activeTab,
    selectedCompanyProfileId,
    selectedCustomerCategoryId,
    setActiveTab,
    setSelectedCompanyProfileId,
    setSelectedCustomerCategoryId
  } = useSettingsViewStore();
  const [workspaceSettings, setWorkspaceSettings] = useState<WorkspaceSettingsRecord | null>(null);
  const [companyProfiles, setCompanyProfiles] = useState<CompanyProfileCollectionRecord | null>(null);
  const [customerCategories, setCustomerCategories] = useState<CustomerCategoryCollectionRecord | null>(null);
  const [contractTemplates, setContractTemplates] = useState<ContractTemplateCollectionRecord | null>(null);
  const [companyProfileDraft, setCompanyProfileDraft] = useState<CompanyProfileRecord | null>(null);
  const [customerCategoryDraft, setCustomerCategoryDraft] = useState<CustomerCategoryRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isSavingWorkspace, setIsSavingWorkspace] = useState(false);
  const [isSavingCompanyProfile, setIsSavingCompanyProfile] = useState(false);
  const [isSavingCustomerCategory, setIsSavingCustomerCategory] = useState(false);
  const [localEnvironmentSecretsDraft, setLocalEnvironmentSecretsDraft] = useState({
    mysqlPassword: '',
    wordpressAdminPassword: ''
  });
  const [paymentSecretsDraft, setPaymentSecretsDraft] = useState({
    stripeSecretKey: '',
    stripeWebhookSecret: '',
    websiteApiToken: ''
  });
  const [integrationSecretsDraft, setIntegrationSecretsDraft] = useState({
    oblioApiSecret: ''
  });
  const [oblioDiagnostics, setOblioDiagnostics] = useState<OblioDiagnosticsRecord | null>(null);
  const [isTestingOblio, setIsTestingOblio] = useState(false);
  const [bridgeStatus, setBridgeStatus] = useState<ThemeBridgeStatusRecord | null>(null);
  const [isRefreshingBridge, setIsRefreshingBridge] = useState(false);
  const [localizationDraft, setLocalizationDraft] = useState({
    locale: shellSettings.locale,
    navigationDensity: shellPreferences.navigationDensity,
    reduceMotion: shellPreferences.reduceMotion
  });
  const [aiModels, setAiModels] = useState<AiModelRecord[]>([]);
  const [aiKeyStatus, setAiKeyStatus] = useState<AiProviderKeyStatusRecord>(EMPTY_AI_KEY_STATUS);
  const [aiKeyDraft, setAiKeyDraft] = useState<Record<AiProviderId, string>>({ anthropic: '', openai: '' });
  const [isAiBusy, setIsAiBusy] = useState(false);

  useEffect(() => {
    let active = true;

    Promise.all([getWorkspaceSettings(), listCompanyProfiles(), listCustomerCategories(), listContractTemplates()])
      .then(([nextWorkspaceSettings, nextCompanyProfiles, nextCustomerCategories, nextContractTemplates]) => {
        if (!active) {
          return;
        }

        setWorkspaceSettings(nextWorkspaceSettings);
        setCompanyProfiles(nextCompanyProfiles);
        setCustomerCategories(nextCustomerCategories);
        setContractTemplates(nextContractTemplates);

        const nextSelectedId = selectedCompanyProfileId ?? nextCompanyProfiles.companyProfiles[0]?.id ?? null;
        setSelectedCompanyProfileId(nextSelectedId);
        setCompanyProfileDraft(
          selectCompanyProfileDraft(
            nextCompanyProfiles.companyProfiles.find((profile) => profile.id === nextSelectedId) ?? null,
            nextWorkspaceSettings
          )
        );

        const nextSelectedCategoryId =
          selectedCustomerCategoryId ?? nextCustomerCategories.customerCategories[0]?.id ?? null;
        setSelectedCustomerCategoryId(nextSelectedCategoryId);
        setCustomerCategoryDraft(
          selectCustomerCategoryDraft(
            nextCustomerCategories.customerCategories.find((category) => category.id === nextSelectedCategoryId) ?? null
          )
        );
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Settings unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [selectedCompanyProfileId, selectedCustomerCategoryId, setSelectedCompanyProfileId, setSelectedCustomerCategoryId]);

  useEffect(() => {
    if (location.pathname === '/settings/company-profiles' && activeTab !== 'companyProfiles') {
      setActiveTab('companyProfiles');
      return;
    }

    if (location.pathname === '/settings/customer-types' && activeTab !== 'customerTypes') {
      setActiveTab('customerTypes');
      return;
    }

    if (location.pathname === '/settings/payments' && activeTab !== 'payments') {
      setActiveTab('payments');
      return;
    }

    if (location.pathname === '/settings' && (activeTab === 'companyProfiles' || activeTab === 'customerTypes' || activeTab === 'payments')) {
      setActiveTab('general');
    }
  }, [activeTab, location.pathname, setActiveTab]);

  useEffect(() => {
    setLocalizationDraft({
      locale: shellSettings.locale,
      navigationDensity: shellPreferences.navigationDensity,
      reduceMotion: shellPreferences.reduceMotion
    });
  }, [shellPreferences.navigationDensity, shellPreferences.reduceMotion, shellSettings.locale]);

  useEffect(() => {
    if (activeTab !== 'avi') {
      return;
    }

    let active = true;
    void (async () => {
      try {
        const [models, status] = await Promise.all([listAiModels(), getAiProviderKeyStatus()]);
        if (!active) {
          return;
        }
        setAiModels(models);
        setAiKeyStatus(status);
      } catch (nextError) {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'AI settings unavailable.');
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'localEnvironment') {
      return;
    }

    let active = true;
    getWordPressBridgeStatus()
      .then((nextStatus) => {
        if (active) {
          setBridgeStatus(nextStatus);
        }
      })
      .catch(() => {
        // Status fetch failures are rendered inline via the bundled fallback strip.
      });

    return () => {
      active = false;
    };
  }, [activeTab]);

  const settingsTabs = useMemo(() => selectSettingsTabs(), []);

  if (error) {
    return (
      <div className="av-page">
        <EmptyState title={t('settings.title')} description={error} />
      </div>
    );
  }

  if (!workspaceSettings || !companyProfiles || !customerCategories || !contractTemplates || !companyProfileDraft || !customerCategoryDraft) {
    return <div className="av-page" />;
  }

  const currentWorkspaceSettings = workspaceSettings;
  const currentCompanyProfiles = companyProfiles;
  const currentCustomerCategories = customerCategories;
  const currentContractTemplates = contractTemplates;
  const companyProfileOptions = currentCompanyProfiles.companyProfiles.map((profile) => ({
    value: profile.id,
    label: profile.name
  }));
  const contractTemplateOptions = [
    { value: '', label: t('common.notSet') },
    ...currentContractTemplates.templates.map((template) => ({
      value: template.id,
      label: template.name
    }))
  ];
  const aiModelOptions = aiModels.map((model) => ({
    value: model.id,
    label: `${model.provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} - ${model.label}`
  }));
  const aiTaskModelOptions = [
    { value: '', label: t('settings.options.aiTaskModel.useDefault') },
    ...aiModelOptions
  ];
  const companyProfileDraftId = companyProfileDraft.id || selectedCompanyProfileId || '';
  const customerCategoryDraftId = customerCategoryDraft.id || selectedCustomerCategoryId || '';

  async function handleSaveWorkspaceSettings(update: Parameters<typeof updateWorkspaceSettings>[0]): Promise<void> {
    setIsSavingWorkspace(true);
    setSaveMessage(null);

    try {
      const nextWorkspaceSettings = await updateWorkspaceSettings(update);
      setWorkspaceSettings(nextWorkspaceSettings);
      setSaveMessage(t('settings.messages.workspaceSaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Workspace settings could not be saved.');
    } finally {
      setIsSavingWorkspace(false);
    }
  }

  async function handleSaveLocalEnvironment(): Promise<void> {
    setIsSavingWorkspace(true);
    setSaveMessage(null);

    try {
      const nextWorkspaceSettings = await updateWorkspaceSettings({ localEnvironment: currentWorkspaceSettings.localEnvironment });
      const mysqlPassword = localEnvironmentSecretsDraft.mysqlPassword.trim();
      const wordpressAdminPassword = localEnvironmentSecretsDraft.wordpressAdminPassword.trim();

      const shouldUpdateSecrets = Boolean(mysqlPassword || wordpressAdminPassword);

      const nextSettingsWithSecrets = shouldUpdateSecrets
        ? await updateWorkspaceLocalEnvironmentSecrets({
            ...(mysqlPassword ? { mysqlPassword } : {}),
            ...(wordpressAdminPassword ? { wordpressAdminPassword } : {})
          })
        : nextWorkspaceSettings;

      setWorkspaceSettings(nextSettingsWithSecrets);
      setLocalEnvironmentSecretsDraft({ mysqlPassword: '', wordpressAdminPassword: '' });
      setSaveMessage(t('settings.messages.workspaceSaved'));

      try {
        const nextStatus = await refreshWordPressBridgeStatus();
        setBridgeStatus(nextStatus);
      } catch {
        // Bridge revalidation surface is handled through its own status strip.
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Workspace settings could not be saved.');
    } finally {
      setIsSavingWorkspace(false);
    }
  }

  async function handleClearLocalEnvironmentSecret(kind: 'mysqlPassword' | 'wordpressAdminPassword'): Promise<void> {
    setIsSavingWorkspace(true);
    setSaveMessage(null);

    try {
      const nextWorkspaceSettings = await updateWorkspaceLocalEnvironmentSecrets({
        [kind]: null
      });
      setWorkspaceSettings(nextWorkspaceSettings);
      setLocalEnvironmentSecretsDraft((current) => ({ ...current, [kind]: '' }));
      setSaveMessage(t('settings.messages.workspaceSaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Workspace secrets could not be saved.');
    } finally {
      setIsSavingWorkspace(false);
    }
  }

  async function handleSavePayments(): Promise<void> {
    setIsSavingWorkspace(true);
    setSaveMessage(null);

    try {
      const nextWorkspaceSettings = await updateWorkspaceSettings({ payments: currentWorkspaceSettings.payments });
      const stripeSecretKey = paymentSecretsDraft.stripeSecretKey.trim();
      const stripeWebhookSecret = paymentSecretsDraft.stripeWebhookSecret.trim();
      const websiteApiToken = paymentSecretsDraft.websiteApiToken.trim();
      const shouldUpdateSecrets = Boolean(stripeSecretKey || stripeWebhookSecret || websiteApiToken);
      const nextSettingsWithSecrets = shouldUpdateSecrets
        ? await updateWorkspacePaymentSecrets({
            ...(stripeSecretKey ? { stripeSecretKey } : {}),
            ...(stripeWebhookSecret ? { stripeWebhookSecret } : {}),
            ...(websiteApiToken ? { websiteApiToken } : {})
          })
        : nextWorkspaceSettings;

      setWorkspaceSettings(nextSettingsWithSecrets);
      setPaymentSecretsDraft({ stripeSecretKey: '', stripeWebhookSecret: '', websiteApiToken: '' });
      setSaveMessage(t('settings.messages.workspaceSaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Payment settings could not be saved.');
    } finally {
      setIsSavingWorkspace(false);
    }
  }

  async function handleClearPaymentSecret(kind: 'stripeSecretKey' | 'stripeWebhookSecret' | 'websiteApiToken'): Promise<void> {
    setIsSavingWorkspace(true);
    setSaveMessage(null);

    try {
      const nextWorkspaceSettings = await updateWorkspacePaymentSecrets({
        [kind]: null
      });
      setWorkspaceSettings(nextWorkspaceSettings);
      setPaymentSecretsDraft((current) => ({ ...current, [kind]: '' }));
      setSaveMessage(t('settings.messages.workspaceSaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Payment secrets could not be saved.');
    } finally {
      setIsSavingWorkspace(false);
    }
  }

  async function handleSaveIntegrations(): Promise<void> {
    setIsSavingWorkspace(true);
    setSaveMessage(null);

    try {
      const nextWorkspaceSettings = await updateWorkspaceSettings({ integrations: currentWorkspaceSettings.integrations });
      const oblioApiSecret = integrationSecretsDraft.oblioApiSecret.trim();
      const nextSettingsWithSecrets = oblioApiSecret
        ? await updateWorkspaceIntegrationSecrets({ oblioApiSecret })
        : nextWorkspaceSettings;

      setWorkspaceSettings(nextSettingsWithSecrets);
      setIntegrationSecretsDraft({ oblioApiSecret: '' });
      setSaveMessage(t('settings.messages.workspaceSaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Integration settings could not be saved.');
    } finally {
      setIsSavingWorkspace(false);
    }
  }

  async function handleClearIntegrationSecret(): Promise<void> {
    setIsSavingWorkspace(true);
    setSaveMessage(null);

    try {
      const nextWorkspaceSettings = await updateWorkspaceIntegrationSecrets({ oblioApiSecret: null });
      setWorkspaceSettings(nextWorkspaceSettings);
      setIntegrationSecretsDraft({ oblioApiSecret: '' });
      setSaveMessage(t('settings.messages.workspaceSaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Integration secrets could not be saved.');
    } finally {
      setIsSavingWorkspace(false);
    }
  }

  async function handleTestOblioConnection(refresh = false): Promise<void> {
    setIsTestingOblio(true);
    setSaveMessage(null);

    try {
      const diagnostics = refresh ? await refreshOblioNomenclature() : await testOblioConnection();
      setOblioDiagnostics(diagnostics);
      setSaveMessage(diagnostics.error ?? t('settings.oblio.connectionChecked'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Oblio connection could not be checked.');
    } finally {
      setIsTestingOblio(false);
    }
  }

  async function handleValidateBridge(): Promise<void> {
    setIsRefreshingBridge(true);
    setSaveMessage(null);

    try {
      const nextStatus = await refreshWordPressBridgeStatus();
      setBridgeStatus(nextStatus);
      setSaveMessage(t('settings.messages.bridgeValidated'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('settings.messages.bridgeUnavailable'));
    } finally {
      setIsRefreshingBridge(false);
    }
  }

  async function handleRefreshBridgeContract(): Promise<void> {
    setIsRefreshingBridge(true);
    setSaveMessage(null);

    try {
      await getWordPressBridgeContract();
      const nextStatus = await getWordPressBridgeStatus();
      setBridgeStatus(nextStatus);
      setSaveMessage(t('settings.messages.bridgeContractRefreshed'));
    } catch (nextError) {
      const nextStatus = await getWordPressBridgeStatus().catch(() => null);
      if (nextStatus) {
        setBridgeStatus(nextStatus);
      }
      setError(nextError instanceof Error ? nextError.message : t('settings.messages.bridgeUnavailable'));
    } finally {
      setIsRefreshingBridge(false);
    }
  }

  async function handlePickWordPressPath(
    field: 'wordpressBaseThemePath' | 'wordpressCorePluginPath',
    titleKey: string
  ): Promise<void> {
    const defaultPath =
      field === 'wordpressBaseThemePath'
        ? currentWorkspaceSettings.localEnvironment.wordpressBaseThemePath
        : currentWorkspaceSettings.localEnvironment.wordpressCorePluginPath;

    const selected = await pickDirectory({
      title: t(titleKey),
      defaultPath: defaultPath.trim().length > 0 ? defaultPath : null
    });

    if (!selected) {
      return;
    }

    setWorkspaceSettings((current) =>
      current
        ? {
            ...current,
            localEnvironment: {
              ...current.localEnvironment,
              [field]: selected
            }
          }
        : current
    );
  }

  async function handleSaveLocalization(): Promise<void> {
    setSaveMessage(null);

    try {
      await Promise.all([
        saveSettings({ locale: localizationDraft.locale }),
        savePreferences({
          navigationDensity: localizationDraft.navigationDensity,
          reduceMotion: localizationDraft.reduceMotion
        })
      ]);
      setSaveMessage(t('settings.messages.localizationSaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Localization settings could not be saved.');
    }
  }

  async function handleSaveCompanyProfile(): Promise<void> {
    setIsSavingCompanyProfile(true);
    setSaveMessage(null);

    try {
      const draft = companyProfileDraft;

      if (!draft) {
        return;
      }

      const nextCollection = await saveCompanyProfile({
        ...(draft.id ? { id: draft.id } : {}),
        name: draft.name,
        legalName: draft.legalName,
        cui: draft.cui,
        registrationNumber: toNullable(draft.registrationNumber ?? ''),
        vatPayer: draft.vatPayer,
        addressLine: draft.addressLine,
        city: toNullable(draft.city ?? ''),
        county: toNullable(draft.county ?? ''),
        postalCode: toNullable(draft.postalCode ?? ''),
        country: draft.country,
        email: toNullable(draft.email ?? ''),
        phone: toNullable(draft.phone ?? ''),
        bankName: toNullable(draft.bankName ?? ''),
        iban: toNullable(draft.iban ?? ''),
        swift: toNullable(draft.swift ?? ''),
        invoiceSeries: toNullable(draft.invoiceSeries ?? ''),
        defaultCurrency: draft.defaultCurrency,
        contractSignatureBlock: toNullable(draft.contractSignatureBlock ?? ''),
        isDefault: draft.isDefault
      });

      setCompanyProfiles(nextCollection);
      const persistedProfile =
        nextCollection.companyProfiles.find((profile) => profile.name === draft.name && profile.cui === draft.cui) ??
        nextCollection.companyProfiles.find((profile) => profile.id === draft.id) ??
        nextCollection.companyProfiles[0] ??
        null;

      setSelectedCompanyProfileId(persistedProfile?.id ?? null);
      setCompanyProfileDraft(selectCompanyProfileDraft(persistedProfile, currentWorkspaceSettings));
      setSaveMessage(t('settings.messages.companyProfileSaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Company profile could not be saved.');
    } finally {
      setIsSavingCompanyProfile(false);
    }
  }

  async function handleSaveCustomerCategory(archivedAtOverride?: string | null): Promise<void> {
    setIsSavingCustomerCategory(true);
    setSaveMessage(null);

    try {
      const draft = customerCategoryDraft;

      if (!draft) {
        return;
      }

      const nextCollection = await saveCustomerCategory({
        ...(draft.id ? { id: draft.id } : {}),
        name: draft.name,
        ...(draft.slug.trim() ? { slug: draft.slug.trim() } : {}),
        iconName: normalizeBusinessIconName(draft.iconName),
        sortOrder: Number(draft.sortOrder) || 0,
        archivedAt: archivedAtOverride !== undefined ? archivedAtOverride : draft.archivedAt
      });

      setCustomerCategories(nextCollection);
      const persistedCategory =
        nextCollection.customerCategories.find((category) => category.id === draft.id) ??
        nextCollection.customerCategories.find((category) => category.name === draft.name) ??
        nextCollection.customerCategories[0] ??
        null;

      setSelectedCustomerCategoryId(persistedCategory?.id ?? null);
      setCustomerCategoryDraft(selectCustomerCategoryDraft(persistedCategory));
      setSaveMessage(t('settings.messages.customerCategorySaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Customer category could not be saved.');
    } finally {
      setIsSavingCustomerCategory(false);
    }
  }

  async function handleSaveAiKey(provider: AiProviderId): Promise<void> {
    const value = aiKeyDraft[provider].trim();

    if (value.length === 0) {
      return;
    }

    setIsAiBusy(true);
    setSaveMessage(null);

    try {
      const nextStatus = await setAiProviderKey({ provider, apiKey: value });
      setAiKeyStatus(nextStatus);
      setAiKeyDraft((current) => ({ ...current, [provider]: '' }));
      setSaveMessage(t('settings.messages.aiKeySaved'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'AI key could not be saved.');
    } finally {
      setIsAiBusy(false);
    }
  }

  async function handleClearAiKey(provider: AiProviderId): Promise<void> {
    setIsAiBusy(true);
    setSaveMessage(null);

    try {
      const nextStatus = await setAiProviderKey({ provider, apiKey: null });
      setAiKeyStatus(nextStatus);
      setAiKeyDraft((current) => ({ ...current, [provider]: '' }));
      setSaveMessage(t('settings.messages.aiKeyCleared'));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'AI key could not be cleared.');
    } finally {
      setIsAiBusy(false);
    }
  }

  async function handleTestAiKey(provider: AiProviderId): Promise<void> {
    setIsAiBusy(true);
    setSaveMessage(null);

    try {
      const result = await testAiProviderKey({ provider });
      if (result.ok) {
        setSaveMessage(t('settings.messages.aiKeyTestOk', { latency: String(result.latencyMs) }));
      } else {
        setError(result.error ?? 'AI key test failed.');
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'AI key test failed.');
    } finally {
      setIsAiBusy(false);
    }
  }

  async function handleClearAiCache(): Promise<void> {
    setIsAiBusy(true);
    setSaveMessage(null);

    try {
      const result = await clearAiCache();
      setSaveMessage(t('settings.messages.aiCacheCleared', { count: String(result.deleted) }));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'AI cache could not be cleared.');
    } finally {
      setIsAiBusy(false);
    }
  }

  function renderWorkspaceActions(onClick: () => void) {
    return (
      <div className="av-toolbar-group">
        <button className="av-primary-button" disabled={isSavingWorkspace} type="button" onClick={onClick}>
          {t('settings.actions.save')}
        </button>
      </div>
    );
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        actions={saveMessage ? <div className="av-inline-success">{saveMessage}</div> : null}
      />

      <div className="av-editor-page__tabs">
        <EditorTabs
          tabs={settingsTabs.map((tab) => ({
            id: tab.id,
            label: t(tab.labelKey),
            icon: tab.icon
          }))}
          value={activeTab}
          onChange={(tab) => {
            setActiveTab(tab);
            void navigate(
              tab === 'companyProfiles'
                ? '/settings/company-profiles'
                : tab === 'customerTypes'
                  ? '/settings/customer-types'
                  : tab === 'payments'
                    ? '/settings/payments'
                  : '/settings'
            );
          }}
        />
      </div>

      {activeTab === 'general' ? (
        <PanelCard title={t('settings.groups.general')} actions={renderWorkspaceActions(() => void handleSaveWorkspaceSettings({ general: workspaceSettings.general }))}>
          <div className="av-form-grid">
            <label className="av-form-field">
              <span>{t('settings.fields.agencyWorkspaceName')}</span>
              <TextField
                value={workspaceSettings.general.agencyWorkspaceName}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          general: {
                            ...current.general,
                            agencyWorkspaceName: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.defaultCurrency')}</span>
              <TextField
                value={workspaceSettings.general.defaultCurrency}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          general: {
                            ...current.general,
                            defaultCurrency: value.toUpperCase()
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.defaultLanguage')}</span>
              <SelectField
                options={[
                  { value: 'ro-RO', label: 'Romana' },
                  { value: 'en-US', label: 'English' }
                ]}
                value={workspaceSettings.general.defaultLanguage}
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          general: {
                            ...current.general,
                            defaultLanguage: event.target.value as 'ro-RO' | 'en-US'
                          }
                        }
                      : current
                  )
                }
              />
            </label>
          </div>
        </PanelCard>
      ) : null}

      {activeTab === 'companyProfiles' ? (
        <SplitWorkspaceLayout
          main={
            <PanelCard
              title={t('settings.groups.companyProfiles')}
              actions={
                <div className="av-toolbar-group">
                  <button
                    className="av-secondary-button"
                    type="button"
                    onClick={() => {
                      setSelectedCompanyProfileId(null);
                  setCompanyProfileDraft(selectCompanyProfileDraft(null, currentWorkspaceSettings));
                }}
              >
                {t('settings.actions.newCompanyProfile')}
                  </button>
                  <button className="av-primary-button" disabled={isSavingCompanyProfile} type="button" onClick={() => void handleSaveCompanyProfile()}>
                    {t('settings.actions.saveCompanyProfile')}
                  </button>
                </div>
              }
            >
              <div className="av-form-grid">
                <label className="av-form-field">
                  <span>{t('settings.fields.profileName')}</span>
                  <TextField value={companyProfileDraft.name} onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, name: value } : current))} />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.profileLegalName')}</span>
                  <TextField
                    value={companyProfileDraft.legalName}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, legalName: value } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.cui')}</span>
                  <TextField value={companyProfileDraft.cui} onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, cui: value } : current))} />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.registrationNumber')}</span>
                  <TextField
                    value={companyProfileDraft.registrationNumber ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, registrationNumber: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.addressLine')}</span>
                  <TextField
                    value={companyProfileDraft.addressLine}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, addressLine: value } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.city')}</span>
                  <TextField
                    value={companyProfileDraft.city ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, city: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.county')}</span>
                  <TextField
                    value={companyProfileDraft.county ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, county: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.country')}</span>
                  <TextField
                    value={companyProfileDraft.country}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, country: value } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.email')}</span>
                  <TextField
                    value={companyProfileDraft.email ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, email: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.phone')}</span>
                  <TextField
                    value={companyProfileDraft.phone ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, phone: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.bankName')}</span>
                  <TextField
                    value={companyProfileDraft.bankName ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, bankName: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.iban')}</span>
                  <TextField
                    value={companyProfileDraft.iban ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, iban: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.swift')}</span>
                  <TextField
                    value={companyProfileDraft.swift ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, swift: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.invoiceSeries')}</span>
                  <TextField
                    value={companyProfileDraft.invoiceSeries ?? ''}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, invoiceSeries: value || null } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.defaultCurrency')}</span>
                  <TextField
                    value={companyProfileDraft.defaultCurrency}
                    onValueChange={(value) => setCompanyProfileDraft((current) => (current ? { ...current, defaultCurrency: value.toUpperCase() } : current))}
                  />
                </label>
              </div>
              <div className="av-form-field">
                <span>{t('settings.fields.contractSignatureBlock')}</span>
                <TextAreaField
                  rows={4}
                  value={companyProfileDraft.contractSignatureBlock ?? ''}
                  onValueChange={(value) =>
                    setCompanyProfileDraft((current) => (current ? { ...current, contractSignatureBlock: value || null } : current))
                  }
                />
              </div>
              <div className="av-inline-metadata">
                <label className="av-checkbox-field">
                  <input
                    checked={companyProfileDraft.vatPayer}
                    type="checkbox"
                    onChange={(event) => setCompanyProfileDraft((current) => (current ? { ...current, vatPayer: event.target.checked } : current))}
                  />
                  <span>{t('settings.fields.vatPayer')}</span>
                </label>
                <label className="av-checkbox-field">
                  <input
                    checked={companyProfileDraft.isDefault}
                    type="checkbox"
                    onChange={(event) => setCompanyProfileDraft((current) => (current ? { ...current, isDefault: event.target.checked } : current))}
                  />
                  <span>{t('settings.fields.isDefault')}</span>
                </label>
              </div>
            </PanelCard>
          }
          rail={
            <PanelCard title={t('settings.groups.companyProfiles')}>
              <div className="av-summary-stack">
                {companyProfiles.companyProfiles.map((profile) => (
                  <button
                    key={profile.id}
                    className={['av-secondary-button av-secondary-button--full', profile.id === companyProfileDraftId ? 'is-active' : ''].filter(Boolean).join(' ')}
                    type="button"
                    onClick={() => {
                      setSelectedCompanyProfileId(profile.id);
                      setCompanyProfileDraft(selectCompanyProfileDraft(profile, workspaceSettings));
                    }}
                  >
                    <span>{profile.name}</span>
                  </button>
                ))}
              </div>
            </PanelCard>
          }
        />
      ) : null}

      {activeTab === 'customerTypes' ? (
        <SplitWorkspaceLayout
          main={
            <PanelCard
              title={t('settings.groups.customerTypes')}
              actions={
                <div className="av-toolbar-group">
                  <button
                    className="av-secondary-button"
                    type="button"
                    onClick={() => {
                      setSelectedCustomerCategoryId(null);
                      setCustomerCategoryDraft(selectCustomerCategoryDraft(null));
                    }}
                  >
                    {t('settings.actions.newCustomerCategory')}
                  </button>
                  <button
                    className="av-secondary-button"
                    disabled={!customerCategoryDraft.id || isSavingCustomerCategory}
                    type="button"
                    onClick={() => void handleSaveCustomerCategory(customerCategoryDraft.archivedAt ? null : new Date().toISOString())}
                  >
                    {customerCategoryDraft.archivedAt ? t('settings.actions.unarchiveCustomerCategory') : t('settings.actions.archiveCustomerCategory')}
                  </button>
                  <button className="av-primary-button" disabled={isSavingCustomerCategory} type="button" onClick={() => void handleSaveCustomerCategory()}>
                    {t('settings.actions.saveCustomerCategory')}
                  </button>
                </div>
              }
            >
              <div className="av-form-grid">
                <label className="av-form-field">
                  <span>{t('settings.fields.customerCategoryName')}</span>
                  <TextField
                    value={customerCategoryDraft.name}
                    onValueChange={(value) => setCustomerCategoryDraft((current) => (current ? { ...current, name: value } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.customerCategorySlug')}</span>
                  <TextField
                    value={customerCategoryDraft.slug}
                    onValueChange={(value) => setCustomerCategoryDraft((current) => (current ? { ...current, slug: value } : current))}
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.customerCategoryIcon')}</span>
                  <IconPickerField
                    clearLabel={t('common.clear')}
                    emptyLabel={t('common.noResults')}
                    labelForIcon={t}
                    placeholder={t('settings.fields.customerCategoryIcon')}
                    searchPlaceholder={t('settings.customerTypes.searchIcon')}
                    value={customerCategoryDraft.iconName}
                    onValueChange={(value) =>
                      setCustomerCategoryDraft((current) => (current ? { ...current, iconName: value } : current))
                    }
                  />
                </label>
                <label className="av-form-field">
                  <span>{t('settings.fields.customerCategorySortOrder')}</span>
                  <input
                    className="av-input"
                    min={0}
                    type="number"
                    value={customerCategoryDraft.sortOrder}
                    onChange={(event) =>
                      setCustomerCategoryDraft((current) =>
                        current ? { ...current, sortOrder: Number(event.target.value) } : current
                      )
                    }
                  />
                </label>
              </div>
              <div className="av-summary-stack">
                <span>{t('settings.customerTypes.preview')}</span>
                <span className="av-category-chip">
                  <Icon name={normalizeBusinessIconName(customerCategoryDraft.iconName)} />
                  <span>{customerCategoryDraft.name || t('settings.customerTypes.untitled')}</span>
                  {customerCategoryDraft.archivedAt ? <em>{t('common.archived')}</em> : null}
                </span>
              </div>
            </PanelCard>
          }
          rail={
            <PanelCard title={t('settings.groups.customerTypes')}>
              <div className="av-summary-stack">
                {currentCustomerCategories.customerCategories.map((category) => (
                  <button
                    key={category.id}
                    className={[
                      'av-secondary-button av-secondary-button--full',
                      category.id === customerCategoryDraftId ? 'is-active' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    type="button"
                    onClick={() => {
                      setSelectedCustomerCategoryId(category.id);
                      setCustomerCategoryDraft(selectCustomerCategoryDraft(category));
                    }}
                  >
                    <Icon name={normalizeBusinessIconName(category.iconName)} />
                    <span>{category.name}</span>
                    {category.archivedAt ? <em>{t('common.archived')}</em> : null}
                  </button>
                ))}
              </div>
            </PanelCard>
          }
        />
      ) : null}

      {activeTab === 'invoicing' ? (
        <PanelCard title={t('settings.groups.invoicing')} actions={renderWorkspaceActions(() => void handleSaveWorkspaceSettings({ invoicing: workspaceSettings.invoicing }))}>
          <div className="av-form-grid">
            <label className="av-form-field">
              <span>{t('settings.fields.defaultIssuer')}</span>
              <SelectField
                options={companyProfileOptions}
                value={workspaceSettings.invoicing.defaultIssuerCompanyProfileId ?? companyProfileOptions[0]?.value ?? ''}
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          invoicing: {
                            ...current.invoicing,
                            defaultIssuerCompanyProfileId: event.target.value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.defaultSeries')}</span>
              <TextField
                value={workspaceSettings.invoicing.defaultSeries}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          invoicing: {
                            ...current.invoicing,
                            defaultSeries: value.toUpperCase()
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.numberingPrefix')}</span>
              <TextField
                value={workspaceSettings.invoicing.numberingPrefix}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          invoicing: {
                            ...current.invoicing,
                            numberingPrefix: value.toUpperCase()
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.nextNumber')}</span>
              <TextField
                type="number"
                value={String(workspaceSettings.invoicing.nextNumber)}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          invoicing: {
                            ...current.invoicing,
                            nextNumber: Number(value)
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.paymentTermsDays')}</span>
              <TextField
                type="number"
                value={String(workspaceSettings.invoicing.paymentTermsDays)}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          invoicing: {
                            ...current.invoicing,
                            paymentTermsDays: Number(value)
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.defaultVatRate')}</span>
              <TextField
                type="number"
                value={String(workspaceSettings.invoicing.defaultVatRate)}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          invoicing: {
                            ...current.invoicing,
                            defaultVatRate: Number(value)
                          }
                        }
                      : current
                  )
                }
              />
            </label>
          </div>
          <div className="av-form-field">
            <span>{t('settings.fields.pdfFooter')}</span>
            <TextAreaField
              rows={4}
              value={workspaceSettings.invoicing.pdfFooter ?? ''}
              onValueChange={(value) =>
                setWorkspaceSettings((current) =>
                  current
                    ? {
                        ...current,
                        invoicing: {
                          ...current.invoicing,
                          pdfFooter: value || null
                        }
                      }
                    : current
                )
              }
            />
          </div>
        </PanelCard>
      ) : null}

      {activeTab === 'contracts' ? (
        <PanelCard title={t('settings.groups.contracts')} actions={renderWorkspaceActions(() => void handleSaveWorkspaceSettings({ contracts: workspaceSettings.contracts }))}>
          <div className="av-form-grid">
            <label className="av-form-field">
              <span>{t('settings.fields.defaultIssuer')}</span>
              <SelectField
                options={companyProfileOptions}
                value={workspaceSettings.contracts.defaultIssuerCompanyProfileId ?? companyProfileOptions[0]?.value ?? ''}
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          contracts: {
                            ...current.contracts,
                            defaultIssuerCompanyProfileId: event.target.value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.numberingPrefix')}</span>
              <TextField
                value={workspaceSettings.contracts.numberingPrefix}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          contracts: {
                            ...current.contracts,
                            numberingPrefix: value.toUpperCase()
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.nextNumber')}</span>
              <TextField
                type="number"
                value={String(workspaceSettings.contracts.nextNumber)}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          contracts: {
                            ...current.contracts,
                            nextNumber: Number(value)
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.defaultTemplate')}</span>
              <SelectField
                options={contractTemplateOptions}
                value={workspaceSettings.contracts.defaultTemplateId ?? ''}
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          contracts: {
                            ...current.contracts,
                            defaultTemplateId: event.target.value || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
          </div>
          <div className="av-form-field">
            <span>{t('settings.fields.signatureFooter')}</span>
            <TextAreaField
              rows={4}
              value={workspaceSettings.contracts.signatureFooter ?? ''}
              onValueChange={(value) =>
                setWorkspaceSettings((current) =>
                  current
                    ? {
                        ...current,
                        contracts: {
                          ...current.contracts,
                          signatureFooter: value || null
                        }
                      }
                    : current
                )
              }
            />
          </div>
        </PanelCard>
      ) : null}

      {activeTab === 'localEnvironment' ? (
        <PanelCard
          title={t('settings.groups.localEnvironment')}
          actions={renderWorkspaceActions(() => void handleSaveLocalEnvironment())}
        >
          <div className="av-form-grid">
            <label className="av-form-field">
              <span>{t('settings.fields.xamppBasePath')}</span>
              <TextField
                value={workspaceSettings.localEnvironment.xamppBasePath}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          localEnvironment: {
                            ...current.localEnvironment,
                            xamppBasePath: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.defaultLocalDomainSuffix')}</span>
              <TextField
                value={workspaceSettings.localEnvironment.defaultLocalDomainSuffix}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          localEnvironment: {
                            ...current.localEnvironment,
                            defaultLocalDomainSuffix: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.mysqlHost')}</span>
              <TextField
                value={workspaceSettings.localEnvironment.mysqlHost}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          localEnvironment: {
                            ...current.localEnvironment,
                            mysqlHost: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.mysqlPort')}</span>
              <TextField
                type="number"
                value={String(workspaceSettings.localEnvironment.mysqlPort)}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          localEnvironment: {
                            ...current.localEnvironment,
                            mysqlPort: Number(value)
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.mysqlUser')}</span>
              <TextField
                value={workspaceSettings.localEnvironment.mysqlUser}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          localEnvironment: {
                            ...current.localEnvironment,
                            mysqlUser: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.mysqlPassword')}</span>
              <TextField
                type="password"
                value={localEnvironmentSecretsDraft.mysqlPassword}
                onValueChange={(value) =>
                  setLocalEnvironmentSecretsDraft((current) => ({
                    ...current,
                    mysqlPassword: value
                  }))
                }
                placeholder="••••••"
              />
              <div className="av-inline-metadata">
                <StatusBadge
                  label={workspaceSettings.localEnvironment.mysqlPasswordSet ? t('common.saved') : t('common.notSet')}
                  tone={workspaceSettings.localEnvironment.mysqlPasswordSet ? 'success' : 'neutral'}
                />
                <button
                  className="av-secondary-button"
                  type="button"
                  disabled={!workspaceSettings.localEnvironment.mysqlPasswordSet || isSavingWorkspace}
                  onClick={() => void handleClearLocalEnvironmentSecret('mysqlPassword')}
                >
                  {t('settings.actions.clearSecret')}
                </button>
              </div>
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.mysqlCharset')}</span>
              <TextField
                value={workspaceSettings.localEnvironment.mysqlCharset}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          localEnvironment: {
                            ...current.localEnvironment,
                            mysqlCharset: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.wordpressAdminUsername')}</span>
              <TextField
                value={workspaceSettings.localEnvironment.wordpressAdminUsername}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          localEnvironment: {
                            ...current.localEnvironment,
                            wordpressAdminUsername: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.wordpressAdminPassword')}</span>
              <TextField
                type="password"
                value={localEnvironmentSecretsDraft.wordpressAdminPassword}
                onValueChange={(value) =>
                  setLocalEnvironmentSecretsDraft((current) => ({
                    ...current,
                    wordpressAdminPassword: value
                  }))
                }
                placeholder="••••••"
              />
              <div className="av-inline-metadata">
                <StatusBadge
                  label={
                    workspaceSettings.localEnvironment.wordpressAdminPasswordSet ? t('common.saved') : t('common.notSet')
                  }
                  tone={workspaceSettings.localEnvironment.wordpressAdminPasswordSet ? 'success' : 'neutral'}
                />
                <button
                  className="av-secondary-button"
                  type="button"
                  disabled={!workspaceSettings.localEnvironment.wordpressAdminPasswordSet || isSavingWorkspace}
                  onClick={() => void handleClearLocalEnvironmentSecret('wordpressAdminPassword')}
                >
                  {t('settings.actions.clearSecret')}
                </button>
              </div>
            </label>
          </div>
        </PanelCard>
      ) : null}

      {activeTab === 'localEnvironment' ? (
        <PanelCard
          title={t('settings.groups.wordpressPackages')}
          actions={
            <div className="av-inline-metadata">
              <button
                className="av-secondary-button"
                type="button"
                disabled={isRefreshingBridge}
                onClick={() => void handleValidateBridge()}
              >
                {t('settings.actions.validateBridge')}
              </button>
              <button
                className="av-primary-button"
                type="button"
                disabled={isRefreshingBridge}
                onClick={() => void handleRefreshBridgeContract()}
              >
                {t('settings.actions.refreshBridgeContract')}
              </button>
            </div>
          }
        >
          <p className="av-helper-text" style={{ marginTop: 0 }}>
            {t('settings.wordpressPackages.description')}
          </p>
          <div className="av-form-grid">
            <label className="av-form-field">
              <span>{t('settings.fields.wordpressBaseThemePath')}</span>
              <div className="av-inline-metadata">
                <TextField
                  value={workspaceSettings.localEnvironment.wordpressBaseThemePath}
                  onValueChange={(value) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            localEnvironment: {
                              ...current.localEnvironment,
                              wordpressBaseThemePath: value
                            }
                          }
                        : current
                    )
                  }
                />
                <button
                  className="av-secondary-button"
                  type="button"
                  disabled={isSavingWorkspace}
                  onClick={() =>
                    void handlePickWordPressPath(
                      'wordpressBaseThemePath',
                      'settings.wordpressPackages.pickThemeDialogTitle'
                    )
                  }
                >
                  {t('settings.actions.browseFolder')}
                </button>
              </div>
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.wordpressCorePluginPath')}</span>
              <div className="av-inline-metadata">
                <TextField
                  value={workspaceSettings.localEnvironment.wordpressCorePluginPath}
                  onValueChange={(value) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            localEnvironment: {
                              ...current.localEnvironment,
                              wordpressCorePluginPath: value
                            }
                          }
                        : current
                    )
                  }
                />
                <button
                  className="av-secondary-button"
                  type="button"
                  disabled={isSavingWorkspace}
                  onClick={() =>
                    void handlePickWordPressPath(
                      'wordpressCorePluginPath',
                      'settings.wordpressPackages.pickPluginDialogTitle'
                    )
                  }
                >
                  {t('settings.actions.browseFolder')}
                </button>
              </div>
            </label>
          </div>

          {bridgeStatus ? (
            <div className="av-form-grid" style={{ marginTop: '1rem' }}>
              <BridgeSourcePanel
                titleKey="settings.wordpressPackages.themeLabel"
                source={bridgeStatus.theme}
              />
              <BridgeSourcePanel
                titleKey="settings.wordpressPackages.pluginLabel"
                source={bridgeStatus.plugin}
              />
            </div>
          ) : (
            <p className="av-helper-text" style={{ marginTop: '1rem' }}>
              {t('settings.wordpressPackages.loading')}
            </p>
          )}

          {bridgeStatus ? (
            <div className="av-inline-metadata" style={{ marginTop: '0.75rem' }}>
              <StatusBadge
                label={
                  bridgeStatus.isValid
                    ? t('settings.wordpressPackages.bridgeStatusValid')
                    : t('settings.wordpressPackages.bridgeStatusInvalid')
                }
                tone={bridgeStatus.isValid ? 'success' : 'warning'}
              />
              <span className="av-helper-text">
                {bridgeStatus.contractHash
                  ? t('settings.wordpressPackages.contractHash', {
                      hash: bridgeStatus.contractHash.slice(0, 12)
                    })
                  : t('settings.wordpressPackages.contractHashUnavailable')}
              </span>
              <span className="av-helper-text">
                {t('settings.wordpressPackages.validatedAt', {
                  timestamp: bridgeStatus.validatedAt
                })}
              </span>
            </div>
          ) : null}

          {bridgeStatus && bridgeStatus.issues.length > 0 ? (
            <div style={{ marginTop: '0.75rem' }}>
              <p className="av-helper-text">{t('settings.wordpressPackages.issuesTitle')}</p>
              <ul className="av-helper-text" style={{ paddingLeft: '1.25rem', margin: 0 }}>
                {bridgeStatus.issues.map((issue, index) => (
                  <li key={`${issue.code}-${index}`}>
                    <strong>[{issue.severity}]</strong> {issue.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {bridgeStatus && bridgeStatus.issues.length === 0 ? (
            <p className="av-helper-text" style={{ marginTop: '0.75rem' }}>
              {t('settings.wordpressPackages.noIssues')}
            </p>
          ) : null}
        </PanelCard>
      ) : null}

      {activeTab === 'payments' ? (
        <PanelCard
          title={t('settings.groups.payments')}
          actions={renderWorkspaceActions(() => void handleSavePayments())}
        >
          <div className="av-form-grid">
            <label className="av-form-field">
              <span>{t('settings.fields.paymentWebsiteBaseUrl')}</span>
              <TextField
                value={workspaceSettings.payments.websiteBaseUrl}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          payments: {
                            ...current.payments,
                            websiteBaseUrl: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.paymentApiBaseUrl')}</span>
              <TextField
                value={workspaceSettings.payments.apiBaseUrl}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          payments: {
                            ...current.payments,
                            apiBaseUrl: value
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.stripeMode')}</span>
              <SelectField
                options={[
                  { value: 'test', label: t('settings.options.stripeMode.test') },
                  { value: 'live', label: t('settings.options.stripeMode.live') }
                ]}
                value={workspaceSettings.payments.stripeMode}
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          payments: {
                            ...current.payments,
                            stripeMode: event.target.value as 'test' | 'live'
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.paymentDefaultCurrency')}</span>
              <SelectField
                options={['RON', 'EUR', 'USD'].map((currency) => ({ value: currency, label: currency }))}
                value={workspaceSettings.payments.defaultCurrency}
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          payments: {
                            ...current.payments,
                            defaultCurrency: event.target.value as 'RON' | 'EUR' | 'USD'
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.paymentDefaultExpirationDays')}</span>
              <TextField
                type="number"
                min={1}
                value={String(workspaceSettings.payments.defaultExpirationDays)}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          payments: {
                            ...current.payments,
                            defaultExpirationDays: Math.max(1, Number(value) || 1)
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.stripePublishableKey')}</span>
              <TextField
                value={workspaceSettings.payments.stripePublishableKey ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          payments: {
                            ...current.payments,
                            stripePublishableKey: value || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
          </div>

          <div className="av-form-grid" style={{ marginTop: '1rem' }}>
            <label className="av-form-field">
              <span>{t('settings.fields.stripeSecretKey')}</span>
              <TextField
                type="password"
                value={paymentSecretsDraft.stripeSecretKey}
                onValueChange={(stripeSecretKey) => setPaymentSecretsDraft((current) => ({ ...current, stripeSecretKey }))}
              />
              <StatusBadge
                label={workspaceSettings.payments.stripeSecretKeySet ? t('common.saved') : t('common.notSet')}
                tone={workspaceSettings.payments.stripeSecretKeySet ? 'success' : 'neutral'}
              />
              <button
                className="av-secondary-button"
                disabled={!workspaceSettings.payments.stripeSecretKeySet || isSavingWorkspace}
                type="button"
                onClick={() => void handleClearPaymentSecret('stripeSecretKey')}
              >
                {t('common.clear')}
              </button>
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.stripeWebhookSecret')}</span>
              <TextField
                type="password"
                value={paymentSecretsDraft.stripeWebhookSecret}
                onValueChange={(stripeWebhookSecret) => setPaymentSecretsDraft((current) => ({ ...current, stripeWebhookSecret }))}
              />
              <StatusBadge
                label={workspaceSettings.payments.stripeWebhookSecretSet ? t('common.saved') : t('common.notSet')}
                tone={workspaceSettings.payments.stripeWebhookSecretSet ? 'success' : 'neutral'}
              />
              <button
                className="av-secondary-button"
                disabled={!workspaceSettings.payments.stripeWebhookSecretSet || isSavingWorkspace}
                type="button"
                onClick={() => void handleClearPaymentSecret('stripeWebhookSecret')}
              >
                {t('common.clear')}
              </button>
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.websiteApiToken')}</span>
              <TextField
                type="password"
                value={paymentSecretsDraft.websiteApiToken}
                onValueChange={(websiteApiToken) => setPaymentSecretsDraft((current) => ({ ...current, websiteApiToken }))}
              />
              <StatusBadge
                label={workspaceSettings.payments.websiteApiTokenSet ? t('common.saved') : t('common.notSet')}
                tone={workspaceSettings.payments.websiteApiTokenSet ? 'success' : 'neutral'}
              />
              <button
                className="av-secondary-button"
                disabled={!workspaceSettings.payments.websiteApiTokenSet || isSavingWorkspace}
                type="button"
                onClick={() => void handleClearPaymentSecret('websiteApiToken')}
              >
                {t('common.clear')}
              </button>
            </label>
          </div>
          <p className="av-helper-text" style={{ marginTop: '0.75rem' }}>
            {t('settings.payments.nestFlowNote')}
          </p>
        </PanelCard>
      ) : null}

      {activeTab === 'integrations' ? (
        <PanelCard
          title={t('settings.groups.integrations')}
          actions={
            <div className="av-inline-metadata">
              <button
                className="av-secondary-button"
                type="button"
                disabled={isTestingOblio}
                onClick={() => void handleTestOblioConnection(false)}
              >
                {t('settings.actions.testOblio')}
              </button>
              <button
                className="av-secondary-button"
                type="button"
                disabled={isTestingOblio}
                onClick={() => void handleTestOblioConnection(true)}
              >
                {t('settings.actions.refreshOblio')}
              </button>
              {renderWorkspaceActions(() => void handleSaveIntegrations())}
            </div>
          }
        >
          <div className="av-inline-metadata">
            <label className="av-checkbox-field">
              <input
                checked={workspaceSettings.integrations.oblioEnabled}
                type="checkbox"
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioEnabled: event.target.checked
                          }
                        }
                      : current
                  )
                }
              />
              <span>{t('settings.fields.oblioEnabled')}</span>
            </label>
            <label className="av-checkbox-field">
              <input
                checked={workspaceSettings.integrations.eFacturaEnabled}
                type="checkbox"
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            eFacturaEnabled: event.target.checked
                          }
                        }
                      : current
                  )
                }
              />
              <span>{t('settings.fields.eFacturaEnabled')}</span>
            </label>
          </div>
          <div className="av-form-grid">
            <label className="av-form-field">
              <span>{t('settings.fields.oblioClientId')}</span>
              <TextField
                value={workspaceSettings.integrations.oblioClientId ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioClientId: value || null,
                            oblioEmail: null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.oblioApiSecret')}</span>
              <TextField
                type="password"
                value={integrationSecretsDraft.oblioApiSecret}
                onValueChange={(oblioApiSecret) =>
                  setIntegrationSecretsDraft((current) => ({
                    ...current,
                    oblioApiSecret
                  }))
                }
              />
              <div className="av-inline-metadata">
                <StatusBadge
                  label={workspaceSettings.integrations.oblioApiSecretSet ? t('common.saved') : t('common.notSet')}
                  tone={workspaceSettings.integrations.oblioApiSecretSet ? 'success' : 'neutral'}
                />
                <button
                  className="av-secondary-button"
                  type="button"
                  disabled={!workspaceSettings.integrations.oblioApiSecretSet || isSavingWorkspace}
                  onClick={() => void handleClearIntegrationSecret()}
                >
                  {t('common.clear')}
                </button>
              </div>
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.oblioIssuerCompanyName')}</span>
              <TextField
                value={workspaceSettings.integrations.oblioIssuerCompanyName ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioIssuerCompanyName: value || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.oblioIssuerCif')}</span>
              <TextField
                value={workspaceSettings.integrations.oblioIssuerCif ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioIssuerCif: value || null,
                            eFacturaIssuerVat: value || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.oblioIssuerRegistrationNumber')}</span>
              <TextField
                value={workspaceSettings.integrations.oblioIssuerRegistrationNumber ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioIssuerRegistrationNumber: value || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.oblioInvoiceSeries')}</span>
              <TextField
                value={workspaceSettings.integrations.oblioInvoiceSeries ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioInvoiceSeries: value.toUpperCase() || null
                          },
                          invoicing: {
                            ...current.invoicing,
                            defaultSeries: value.toUpperCase() || current.invoicing.defaultSeries,
                            numberingPrefix: value.toUpperCase() || current.invoicing.numberingPrefix
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.oblioProformaSeries')}</span>
              <TextField
                value={workspaceSettings.integrations.oblioProformaSeries ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioProformaSeries: value.toUpperCase() || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.oblioDefaultVatName')}</span>
              <TextField
                value={workspaceSettings.integrations.oblioDefaultVatName ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioDefaultVatName: value || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.oblioDefaultVatPercentage')}</span>
              <TextField
                type="number"
                min={0}
                max={100}
                value={String(workspaceSettings.integrations.oblioDefaultVatPercentage)}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            oblioDefaultVatPercentage: Number(value) || 0
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.eFacturaIssuerVat')}</span>
              <TextField
                value={workspaceSettings.integrations.eFacturaIssuerVat ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            eFacturaIssuerVat: value || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.signingProvider')}</span>
              <SelectField
                options={[
                  { value: 'none', label: t('settings.options.signingProvider.none') },
                  { value: 'reserved', label: t('settings.options.signingProvider.reserved') }
                ]}
                value={workspaceSettings.integrations.signingProvider}
                onChange={(event) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            signingProvider: event.target.value as 'none' | 'reserved'
                          }
                        }
                      : current
                  )
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.signingApiKey')}</span>
              <TextField
                value={workspaceSettings.integrations.signingApiKey ?? ''}
                onValueChange={(value) =>
                  setWorkspaceSettings((current) =>
                    current
                      ? {
                          ...current,
                          integrations: {
                            ...current.integrations,
                            signingApiKey: value || null
                          }
                        }
                      : current
                  )
                }
              />
            </label>
          </div>
          <div className="av-summary-stack" style={{ marginTop: '1rem' }}>
            <StatusBadge
              label={
                oblioDiagnostics?.error
                  ? t('settings.oblio.statusError')
                  : oblioDiagnostics?.configured
                    ? t('settings.oblio.statusReady')
                    : t('settings.oblio.statusPending')
              }
              tone={oblioDiagnostics?.error ? 'warning' : oblioDiagnostics?.configured ? 'success' : 'neutral'}
            />
            <ValuePair
              label={t('settings.oblio.company')}
              value={oblioDiagnostics?.issuerCompanyName ?? workspaceSettings.integrations.oblioIssuerCompanyName ?? t('common.notSet')}
            />
            <ValuePair
              label={t('settings.oblio.invoiceNext')}
              value={
                oblioDiagnostics?.invoiceSeriesName && oblioDiagnostics.invoiceNextNumber
                  ? `${oblioDiagnostics.invoiceSeriesName} ${oblioDiagnostics.invoiceNextNumber}`
                  : t('common.notSet')
              }
            />
            <ValuePair
              label={t('settings.oblio.proforma')}
              value={
                oblioDiagnostics?.hasProformaSeries
                  ? `${oblioDiagnostics.proformaSeriesName ?? ''} ${oblioDiagnostics.proformaNextNumber ?? ''}`.trim()
                  : t('settings.oblio.noProformaSeries')
              }
            />
            {oblioDiagnostics?.error ? <p className="av-form-error">{oblioDiagnostics.error}</p> : null}
          </div>
        </PanelCard>
      ) : null}

      {activeTab === 'avi' ? (
        <>
          <PanelCard
            title={t('settings.groups.avi')}
            actions={renderWorkspaceActions(() => void handleSaveWorkspaceSettings({ avi: workspaceSettings.avi }))}
          >
            <div className="av-form-grid">
              <label className="av-form-field">
                <span>{t('settings.fields.aiDefaultProvider')}</span>
                <SelectField
                  options={[
                    { value: 'anthropic', label: 'Anthropic' },
                    { value: 'openai', label: 'OpenAI' }
                  ]}
                  value={workspaceSettings.avi.defaultProvider}
                  onChange={(event) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            avi: {
                              ...current.avi,
                              defaultProvider: event.target.value as AiProviderId
                            }
                          }
                        : current
                    )
                  }
                />
              </label>
              <label className="av-form-field">
                <span>{t('settings.fields.aiDefaultModel')}</span>
                <SelectField
                  options={aiModelOptions}
                  value={workspaceSettings.avi.defaultModel}
                  onChange={(event) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            avi: {
                              ...current.avi,
                              defaultModel: event.target.value
                            }
                          }
                        : current
                    )
                  }
                />
              </label>
              <label className="av-form-field">
                <span>{t('settings.fields.aiTaskFullDesign')}</span>
                <SelectField
                  options={aiTaskModelOptions}
                  value={workspaceSettings.avi.taskModels.fullDesign ?? ''}
                  onChange={(event) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            avi: {
                              ...current.avi,
                              taskModels: {
                                ...current.avi.taskModels,
                                fullDesign: event.target.value || null
                              }
                            }
                          }
                        : current
                    )
                  }
                />
              </label>
              <label className="av-form-field">
                <span>{t('settings.fields.aiTaskEditSection')}</span>
                <SelectField
                  options={aiTaskModelOptions}
                  value={workspaceSettings.avi.taskModels.editSection ?? ''}
                  onChange={(event) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            avi: {
                              ...current.avi,
                              taskModels: {
                                ...current.avi.taskModels,
                                editSection: event.target.value || null
                              }
                            }
                          }
                        : current
                    )
                  }
                />
              </label>
              <label className="av-form-field">
                <span>{t('settings.fields.aiTaskCopyPolish')}</span>
                <SelectField
                  options={aiTaskModelOptions}
                  value={workspaceSettings.avi.taskModels.copyPolish ?? ''}
                  onChange={(event) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            avi: {
                              ...current.avi,
                              taskModels: {
                                ...current.avi.taskModels,
                                copyPolish: event.target.value || null
                              }
                            }
                          }
                        : current
                    )
                  }
                />
              </label>
            </div>
            <div className="av-inline-metadata">
              <label className="av-checkbox-field">
                <input
                  checked={workspaceSettings.avi.cacheEnabled}
                  type="checkbox"
                  onChange={(event) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            avi: {
                              ...current.avi,
                              cacheEnabled: event.target.checked
                            }
                          }
                        : current
                    )
                  }
                />
                <span>{t('settings.fields.aiCacheEnabled')}</span>
              </label>
              <label className="av-checkbox-field">
                <input
                  checked={workspaceSettings.avi.projectContextEnabled}
                  type="checkbox"
                  onChange={(event) =>
                    setWorkspaceSettings((current) =>
                      current
                        ? {
                            ...current,
                            avi: {
                              ...current.avi,
                              projectContextEnabled: event.target.checked
                            }
                          }
                        : current
                    )
                  }
                />
                <span>{t('settings.fields.projectContextEnabled')}</span>
              </label>
              <button
                className="av-secondary-button"
                type="button"
                disabled={isAiBusy}
                onClick={() => void handleClearAiCache()}
              >
                {t('settings.actions.clearAiCache')}
              </button>
            </div>
          </PanelCard>

          <PanelCard title={t('settings.fields.aiAnthropicKey')}>
            <div className="av-form-grid">
              <label className="av-form-field">
                <span>{t('settings.fields.aiAnthropicKey')}</span>
                <TextField
                  type="password"
                  value={aiKeyDraft.anthropic}
                  placeholder="sk-ant-..."
                  onValueChange={(value) => setAiKeyDraft((current) => ({ ...current, anthropic: value }))}
                />
                <div className="av-inline-metadata">
                  <StatusBadge
                    label={getAiStatusLabel(aiKeyStatus.anthropic, t)}
                    tone={getAiStatusTone(aiKeyStatus.anthropic)}
                  />
                  <button
                    className="av-primary-button"
                    type="button"
                    disabled={isAiBusy || aiKeyDraft.anthropic.trim().length === 0}
                    onClick={() => void handleSaveAiKey('anthropic')}
                  >
                    {t('settings.actions.saveAiKey')}
                  </button>
                  <button
                    className="av-secondary-button"
                    type="button"
                    disabled={isAiBusy || aiKeyStatus.anthropic.state !== 'saved'}
                    onClick={() => void handleTestAiKey('anthropic')}
                  >
                    {t('settings.actions.testAiKey')}
                  </button>
                  <button
                    className="av-secondary-button"
                    type="button"
                    disabled={isAiBusy || aiKeyStatus.anthropic.state === 'not_set'}
                    onClick={() => void handleClearAiKey('anthropic')}
                  >
                    {t('settings.actions.clearAiKey')}
                  </button>
                </div>
                {aiKeyStatus.anthropic.message ? (
                  <p className="av-helper-text">{aiKeyStatus.anthropic.message}</p>
                ) : null}
              </label>
            </div>
          </PanelCard>

          <PanelCard title={t('settings.fields.aiOpenAiKey')}>
            <div className="av-form-grid">
              <label className="av-form-field">
                <span>{t('settings.fields.aiOpenAiKey')}</span>
                <TextField
                  type="password"
                  value={aiKeyDraft.openai}
                  placeholder="sk-..."
                  onValueChange={(value) => setAiKeyDraft((current) => ({ ...current, openai: value }))}
                />
                <div className="av-inline-metadata">
                  <StatusBadge
                    label={getAiStatusLabel(aiKeyStatus.openai, t)}
                    tone={getAiStatusTone(aiKeyStatus.openai)}
                  />
                  <button
                    className="av-primary-button"
                    type="button"
                    disabled={isAiBusy || aiKeyDraft.openai.trim().length === 0}
                    onClick={() => void handleSaveAiKey('openai')}
                  >
                    {t('settings.actions.saveAiKey')}
                  </button>
                  <button
                    className="av-secondary-button"
                    type="button"
                    disabled={isAiBusy || aiKeyStatus.openai.state !== 'saved'}
                    onClick={() => void handleTestAiKey('openai')}
                  >
                    {t('settings.actions.testAiKey')}
                  </button>
                  <button
                    className="av-secondary-button"
                    type="button"
                    disabled={isAiBusy || aiKeyStatus.openai.state === 'not_set'}
                    onClick={() => void handleClearAiKey('openai')}
                  >
                    {t('settings.actions.clearAiKey')}
                  </button>
                </div>
                {aiKeyStatus.openai.message ? (
                  <p className="av-helper-text">{aiKeyStatus.openai.message}</p>
                ) : null}
              </label>
            </div>
          </PanelCard>
        </>
      ) : null}

      {activeTab === 'localization' ? (
        <PanelCard
          title={t('settings.groups.localization')}
          actions={
            <div className="av-toolbar-group">
              <button className="av-primary-button" type="button" onClick={() => void handleSaveLocalization()}>
                {t('settings.actions.save')}
              </button>
            </div>
          }
        >
          <div className="av-form-grid">
            <label className="av-form-field">
              <span>{t('settings.fields.locale')}</span>
              <SelectField
                options={[
                  { value: 'ro-RO', label: 'Romana' },
                  { value: 'en-US', label: 'English' }
                ]}
                value={localizationDraft.locale}
                onChange={(event) =>
                  setLocalizationDraft((current) => ({
                    ...current,
                    locale: event.target.value as 'ro-RO' | 'en-US'
                  }))
                }
              />
            </label>
            <label className="av-form-field">
              <span>{t('settings.fields.navigationDensity')}</span>
              <SelectField
                options={[
                  { value: 'compact', label: t('settings.options.navigationDensity.compact') },
                  { value: 'comfortable', label: t('settings.options.navigationDensity.comfortable') }
                ]}
                value={localizationDraft.navigationDensity}
                onChange={(event) =>
                  setLocalizationDraft((current) => ({
                    ...current,
                    navigationDensity: event.target.value as 'compact' | 'comfortable'
                  }))
                }
              />
            </label>
          </div>
          <label className="av-checkbox-field">
            <input
              checked={localizationDraft.reduceMotion}
              type="checkbox"
              onChange={(event) =>
                setLocalizationDraft((current) => ({
                  ...current,
                  reduceMotion: event.target.checked
                }))
              }
            />
            <span>{t('settings.fields.reduceMotion')}</span>
          </label>
        </PanelCard>
      ) : null}
    </div>
  );
}

interface BridgeSourcePanelProps {
  titleKey: string;
  source: ThemeBridgeSourceStatus;
}

function BridgeSourcePanel({ titleKey, source }: BridgeSourcePanelProps): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="av-panel" style={{ padding: '0.75rem' }}>
      <div className="av-inline-metadata" style={{ marginBottom: '0.5rem' }}>
        <strong>{t(titleKey)}</strong>
        {source.usingBundledFallback ? (
          <StatusBadge label={t('settings.wordpressPackages.bundledFallback')} tone="warning" />
        ) : null}
      </div>
      <p className="av-helper-text" style={{ margin: 0 }}>
        <span style={{ display: 'block' }}>
          <strong>{t('settings.wordpressPackages.resolvedPath')}:</strong> {source.resolvedPath}
        </span>
        <span style={{ display: 'block' }}>
          <strong>{t('settings.wordpressPackages.configuredPath')}:</strong> {source.configuredPath}
        </span>
        <span style={{ display: 'block' }}>
          {source.packageName ?? t('settings.wordpressPackages.packageNameUnknown')}
          {source.version
            ? ` — ${t('settings.wordpressPackages.packageVersion', { version: source.version })}`
            : ''}
        </span>
      </p>
    </div>
  );
}
